// src/stores/MarketplaceStore.ts - COMPLETE UPDATED FILE

import { makeAutoObservable, runInAction } from 'mobx';
import { 
  PublicCatalog, 
  MarketplaceFilters, 
  ContentRecommendation,
  SmartSearchSuggestion,
  SearchContentRequest,
  MarketplaceStats
} from '@/types/marketplace';
import { marketplaceService } from '../services/marketplaceService';
import { RootStore } from '.';

export class MarketplaceStore {
  rootStore: RootStore;
  
  // Content management
  contents: PublicCatalog[] = [];
  featuredContents: PublicCatalog[] = [];
  trendingContents: PublicCatalog[] = [];
  newContents: PublicCatalog[] = [];
  recommendations: ContentRecommendation[] = [];
  
  // Search and filters
  searchQuery: string = '';
  searchSuggestions: SmartSearchSuggestion[] = [];
  activeFilters: MarketplaceFilters = {};
  searchHistory: string[] = [];
  
  // UI states
  isLoading: boolean = false;
  isLoadingFeatured: boolean = false;
  isLoadingTrending: boolean = false;
  isLoadingNew: boolean = false;
  isSearching: boolean = false;
  currentPage: number = 1;
  totalPages: number = 1;
  hasMore: boolean = true;
  
  // Stats and analytics
  stats: MarketplaceStats | null = null;
  
  // Error handling
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
    this.loadSearchHistory();
  }

  // Initialization with better error handling
  async initialize() {
    const initPromises = [
      this.loadFeaturedContents(),
      this.loadTrendingContents(),
      this.loadNewContents(),
      this.loadStats()
    ];

    // Allow partial failures - don't let one failure break everything
    const results = await Promise.allSettled(initPromises);
    
    const failures = results.filter(result => result.status === 'rejected');
    if (failures.length > 0) {
      console.warn(`${failures.length} marketplace initialization tasks failed`);
    }
  }

  // Search functionality
  async searchContents(query: string, filters?: MarketplaceFilters) {
    this.setLoading(true);
    this.setError(null);
    
    try {
      const request: SearchContentRequest = {
        query,
        filters: { ...this.activeFilters, ...filters },
        page: 1,
        limit: 20
      };
      
      const response = await marketplaceService.searchContents(request);
      
      runInAction(() => {
        this.contents = response.data;
        this.totalPages = response.meta.pagination?.pageCount || 1;
        this.currentPage = 1;
        this.hasMore = this.currentPage < this.totalPages;
        this.searchQuery = query;
        
        if (query.trim()) {
          this.addToSearchHistory(query);
        }
      });
    } catch (error) {
      this.setError('KhÃ´ng thá»ƒ tÃ¬m kiáº¿m ná»™i dung');
      console.error('Search error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  async loadMoreContents() {
    if (!this.hasMore || this.isLoading) return;
    
    this.setLoading(true);
    
    try {
      const nextPage = this.currentPage + 1;
      const request: SearchContentRequest = {
        query: this.searchQuery,
        filters: this.activeFilters,
        page: nextPage,
        limit: 20
      };
      
      const response = await marketplaceService.searchContents(request);
      
      runInAction(() => {
        this.contents = [...this.contents, ...response.data];
        this.currentPage = nextPage;
        this.hasMore = nextPage < this.totalPages;
      });
    } catch (error) {
      this.setError('KhÃ´ng thá»ƒ táº£i thÃªm ná»™i dung');
      console.error('Load more error:', error);
    } finally {
      this.setLoading(false);
    }
  }

  // Enhanced content loading with individual loading states
  async loadFeaturedContents() {
    if (this.isLoadingFeatured) return;
    
    runInAction(() => {
      this.isLoadingFeatured = true;
    });

    try {
      const contents = await marketplaceService.getFeaturedContents(10);
      
      runInAction(() => {
        this.featuredContents = contents;
        this.isLoadingFeatured = false;
      });
    } catch (error) {
      console.error('Failed to load featured contents:', error);
      
      runInAction(() => {
        this.featuredContents = []; // Fallback to empty array
        this.isLoadingFeatured = false;
      });
    }
  }

  async loadTrendingContents() {
    if (this.isLoadingTrending) return;
    
    runInAction(() => {
      this.isLoadingTrending = true;
    });

    try {
      const contents = await marketplaceService.getTrendingContents(10);
      
      runInAction(() => {
        this.trendingContents = contents;
        this.isLoadingTrending = false;
      });
    } catch (error) {
      console.error('Failed to load trending contents:', error);
      
      runInAction(() => {
        this.trendingContents = [];
        this.isLoadingTrending = false;
      });
    }
  }

  async loadNewContents() {
    if (this.isLoadingNew) return;
    
    runInAction(() => {
      this.isLoadingNew = true;
    });

    try {
      const contents = await marketplaceService.getNewContents(10);
      
      runInAction(() => {
        this.newContents = contents;
        this.isLoadingNew = false;
      });
    } catch (error) {
      console.error('Failed to load new contents:', error);
      
      runInAction(() => {
        this.newContents = [];
        this.isLoadingNew = false;
      });
    }
  }

  async loadRecommendations(userId: string) {
    try {
      const recommendations = await marketplaceService.getRecommendations(userId);
      runInAction(() => {
        this.recommendations = recommendations;
      });
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      runInAction(() => {
        this.recommendations = [];
      });
    }
  }

  async loadStats() {
    try {
      const stats = await marketplaceService.getMarketplaceStats();
      runInAction(() => {
        this.stats = stats;
      });
    } catch (error) {
      console.error('Failed to load marketplace stats:', error);
      
      runInAction(() => {
        // Set fallback stats instead of null
        this.stats = {
          totalContents: 0,
          totalCreators: 0,
          totalLicenses: 0,
          totalRevenue: 0,
          monthlyGrowth: 0,
          popularCategories: [],
          recentActivity: [],
          topCreators: []
        };
      });
    }
  }

  // ADDED: Missing method alias for backward compatibility
  async getMarketplaceStats(): Promise<MarketplaceStats | null> {
    await this.loadStats();
    return this.stats;
  }

  // Filter management
  updateFilters(filters: Partial<MarketplaceFilters>) {
    this.activeFilters = { ...this.activeFilters, ...filters };
    
    // Re-search with new filters
    if (this.searchQuery || Object.keys(this.activeFilters).length > 0) {
      this.searchContents(this.searchQuery, this.activeFilters);
    }
  }

  clearFilters() {
    this.activeFilters = {};
    this.searchContents(this.searchQuery);
  }

  clearAllFilters() {
    this.activeFilters = {};
    this.searchQuery = '';
    this.contents = [];
  }

  removeFilter(filterKey: keyof MarketplaceFilters) {
    const newFilters = { ...this.activeFilters };
    delete newFilters[filterKey];
    this.updateFilters(newFilters);
  }

  // Content interaction
  async trackContentView(contentId: string) {
    try {
      await marketplaceService.trackContentView(contentId);
    } catch (error) {
      console.error('Failed to track content view:', error);
    }
  }

  async trackContentDownload(contentId: string) {
    try {
      await marketplaceService.trackContentDownload(contentId);
    } catch (error) {
      console.error('Failed to track content download:', error);
    }
  }

  // Quick actions
  async getContentById(contentId: string): Promise<PublicCatalog | null> {
    try {
      return await marketplaceService.getContentById(contentId);
    } catch (error) {
      console.error('Failed to get content:', error);
      return null;
    }
  }

  async getContentsByCategory(category: string, limit: number = 10): Promise<PublicCatalog[]> {
    try {
      return await marketplaceService.getContentsByCategory(category, limit);
    } catch (error) {
      console.error('Failed to get contents by category:', error);
      return [];
    }
  }

  // Collections and curated content
  async getCollection(collectionId: string): Promise<PublicCatalog[]> {
    try {
      return await marketplaceService.getCollection(collectionId);
    } catch (error) {
      console.error('Failed to get collection:', error);
      return [];
    }
  }

  // Search history management
  private addToSearchHistory(query: string) {
    const trimmedQuery = query.trim().toLowerCase();
    if (trimmedQuery && !this.searchHistory.includes(trimmedQuery)) {
      this.searchHistory.unshift(trimmedQuery);
      this.searchHistory = this.searchHistory.slice(0, 10); // Keep only 10 recent searches
      this.saveSearchHistory();
    }
  }

  private saveSearchHistory() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('marketplace_search_history', JSON.stringify(this.searchHistory));
      }
    } catch (error) {
      console.error('Failed to save search history:', error);
    }
  }

  private loadSearchHistory() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem('marketplace_search_history');
        if (saved) {
          this.searchHistory = JSON.parse(saved);
        }
      }
    } catch (error) {
      console.error('Failed to load search history:', error);
      this.searchHistory = [];
    }
  }

  clearSearchHistory() {
    this.searchHistory = [];
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem('marketplace_search_history');
    }
  }

  // State management
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  reset() {
    this.contents = [];
    this.searchQuery = '';
    this.activeFilters = {};
    this.currentPage = 1;
    this.totalPages = 1;
    this.hasMore = true;
    this.error = null;
  }

  // Enhanced getters with better error handling
  get hasContents() {
    return this.contents.length > 0;
  }

  get isEmptySearch() {
    return !this.isLoading && !this.hasContents && this.searchQuery.trim() !== '';
  }

  get activeFilterCount() {
    return Object.values(this.activeFilters).filter(v => 
      Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null
    ).length;
  }

  get hasActiveFilters() {
    return this.activeFilterCount > 0;
  }

  get isInitialized() {
    return this.featuredContents.length > 0 || 
           this.trendingContents.length > 0 || 
           this.newContents.length > 0;
  }

  get isAnyLoading() {
    return this.isLoading || 
           this.isLoadingFeatured || 
           this.isLoadingTrending || 
           this.isLoadingNew;
  }

  // Content categorization helpers
  getContentsByType(type: string) {
    return this.contents.filter(content => content.contentKind === type);
  }

  getContentsByPriceRange(min: number, max: number) {
    return this.contents.filter(content => {
      if (!content.licenseOptions || content.licenseOptions.length === 0) {
        return false;
      }
      const minPrice = Math.min(...content.licenseOptions.map(l => l.price));
      return minPrice >= min && minPrice <= max;
    });
  }

  getPopularContents(limit: number = 10) {
    return [...this.contents]
      .sort((a, b) => (b.viewCount + b.downloadCount) - 
                     (a.viewCount + a.downloadCount))
      .slice(0, limit);
  }

  getHighRatedContents(limit: number = 10) {
    return [...this.contents]
      .filter(content => content.averageRating >= 4.0)
      .sort((a, b) => b.averageRating - a.averageRating)
      .slice(0, limit);
  }

  // Advanced search helpers
  async getSearchSuggestions(query: string) {
    try {
      const suggestions = await marketplaceService.getSearchSuggestions(query);
      runInAction(() => {
        this.searchSuggestions = suggestions;
      });
      return suggestions;
    } catch (error) {
      console.error('Failed to get search suggestions:', error);
      return [];
    }
  }

  // Marketplace health check
  async checkApiHealth(): Promise<boolean> {
    try {
      return await marketplaceService.checkApiHealth();
    } catch (error) {
      console.error('API health check failed:', error);
      return false;
    }
  }

  // Content publishing helpers (for creators)
  async publishToMarketplace(contentId: string, marketplaceData: any) {
    try {
      return await marketplaceService.publishContentToMarketplace(contentId, marketplaceData);
    } catch (error) {
      console.error('Failed to publish to marketplace:', error);
      throw error;
    }
  }

  async updateMarketplaceContent(catalogId: string, updateData: any) {
    try {
      return await marketplaceService.updateMarketplaceContent(catalogId, updateData);
    } catch (error) {
      console.error('Failed to update marketplace content:', error);
      throw error;
    }
  }

  async removeFromMarketplace(catalogId: string) {
    try {
      await marketplaceService.removeFromMarketplace(catalogId);
      // Refresh content lists
      await this.loadFeaturedContents();
      await this.loadTrendingContents();
      await this.loadNewContents();
    } catch (error) {
      console.error('Failed to remove from marketplace:', error);
      throw error;
    }
  }

  
}

export default MarketplaceStore;